import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowDownTrayIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  createEmptyDraftForm,
  listForms,
  listMaintenanceTypes,
  maintenanceTypeLabel,
  setFormDefault,
  WARTUNGEN_REALTIME_DOMAINS,
  type FormListEntry,
} from "@/lib/wartungen";
import { useRealtimeSync } from "@/lib/useRealtime";
import {
  exportMaintenanceForm,
  importMaintenanceForm,
  isMaintenanceFormExportFileName,
} from "@/pages/wartungen/lib/formExportImport";

function normalizeGroupName(rawName: string | null | undefined, formId: string): string {
  const base = String(rawName || `Formular #${formId}`).trim();
  return base.replace(/(?:\s+–\s+Draft)+$/gi, "").trim() || `Formular #${formId}`;
}

export function WartungenFormularePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const categoryKey = (params.get("kategorie") || "").trim();

  const [categoryName, setCategoryName] = useState(categoryKey);
  const [forms, setForms] = useState<FormListEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [exportingFormId, setExportingFormId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!categoryKey) {
      setLoading(false);
      toast.error("Kategorie fehlt in der URL (?kategorie=...).");
      return;
    }
    if (!options?.silent) setLoading(true);
    try {
      const [types, list] = await Promise.all([
        listMaintenanceTypes().catch(() => []),
        listForms(categoryKey),
      ]);
      const active = types.find((t) => String(t.key || "").trim() === categoryKey);
      setCategoryName(active ? maintenanceTypeLabel(active) : categoryKey);
      setForms(list);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Formulare konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [categoryKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useRealtimeSync({
    domains: [...WARTUNGEN_REALTIME_DOMAINS],
    enabled: !!categoryKey,
    paused: showCreate,
    onChange: () => void load({ silent: true }),
  });

  // Nur die jeweils neueste Version pro Formular-Gruppe anzeigen (wie Legacy).
  const visibleForms = useMemo(() => {
    const latestByGroup = new Map<string, FormListEntry>();
    for (const form of forms) {
      const formId = String(form.form_id ?? form.id ?? "").trim();
      const groupName = normalizeGroupName(form.name, formId);
      const existing = latestByGroup.get(groupName);
      const currentVersion = Number((form as { version?: number }).version ?? -1);
      const existingVersion = Number((existing as { version?: number } | undefined)?.version ?? -1);
      if (!existing || currentVersion > existingVersion) latestByGroup.set(groupName, form);
    }
    return Array.from(latestByGroup.values()).sort(
      (a, b) =>
        Number((b as { version?: number }).version ?? -1) -
        Number((a as { version?: number }).version ?? -1),
    );
  }, [forms]);

  async function handleCreate() {
    const name = newName.trim();
    if (!name) {
      setCreateError("Bitte einen Formularnamen eingeben.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const res = await createEmptyDraftForm(categoryKey, name);
      const formId = String(res.form_id || "").trim();
      if (!formId) throw new Error("Formular wurde erstellt, aber ohne form_id.");
      navigate(
        `/wartungen/kategorie-einstellungen?kategorie=${encodeURIComponent(categoryKey)}&form_id=${encodeURIComponent(formId)}`,
      );
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "Neues Formular konnte nicht erstellt werden.");
      setCreating(false);
    }
  }

  async function handleSetDefault(formId: string) {
    try {
      await setFormDefault(formId);
      toast.success("Standardformular wurde aktualisiert.");
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Standardformular konnte nicht gesetzt werden.");
    }
  }

  async function handleExport(formId: string, formName: string) {
    setExportingFormId(formId);
    try {
      await exportMaintenanceForm({ formId, formName, categoryKey });
      toast.success("Formular wurde exportiert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Formular konnte nicht exportiert werden.");
    } finally {
      setExportingFormId(null);
    }
  }

  async function handleImportFile(file: File) {
    if (!categoryKey) {
      toast.error("Kategorie fehlt in der URL (?kategorie=...).");
      return;
    }
    if (!isMaintenanceFormExportFileName(file.name)) {
      toast.error("Bitte eine .json-Exportdatei auswählen.");
      return;
    }
    setImporting(true);
    try {
      const result = await importMaintenanceForm({ categoryKey, file });
      if (result.missingTemplateKeys.length) {
        toast.warning(
          `Import abgeschlossen, aber ${result.missingTemplateKeys.length} Feldvorlage(n) fehlen: ${result.missingTemplateKeys.join(", ")}`,
        );
      } else {
        toast.success(`Formular „${result.formName}“ wurde importiert (${result.fieldCount} Felder).`);
      }
      navigate(
        `/wartungen/kategorie-einstellungen?kategorie=${encodeURIComponent(categoryKey)}&form_id=${encodeURIComponent(result.formId)}`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Formular konnte nicht importiert werden.");
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  }

  return (
    <AppShell
      title={categoryKey ? `Formulare: ${categoryName}` : "Formulare"}
      subtitle={categoryKey ? `Kategorie-Schlüssel: ${categoryKey}` : undefined}
      back={{ to: "/wartungen/einstellungen", label: "zurück zu Kategorien" }}
      actions={
        categoryKey ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImportFile(file);
              }}
            />
            <Button
              type="button"
              variant="secondary"
              loading={importing}
              onClick={() => importInputRef.current?.click()}
            >
              <ArrowUpTrayIcon className="size-4" aria-hidden />
              Importieren
            </Button>
            <Button type="button" onClick={() => { setNewName(""); setCreateError(null); setShowCreate(true); }}>
              Neues Formular
            </Button>
          </div>
        ) : null
      }
    >
      {loading ? (
        <p className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lade Formulare …
        </p>
      ) : visibleForms.length === 0 ? (
        <p className="text-sm text-label-secondary">Noch keine Formulare vorhanden.</p>
      ) : (
        <div className="space-y-3">
          {visibleForms.map((form) => {
            const formId = String(form.form_id ?? form.id ?? "").trim();
            const name = normalizeGroupName(form.name, formId);
            const version = String((form as { version?: unknown }).version ?? "—").trim();
            const status = String(form.status || "—").trim();
            const isDefault = Boolean(form.is_default);
            return (
              <article
                key={formId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-ios border border-separator bg-card p-4 shadow-ios"
              >
                <div>
                  <h3 className="text-base font-semibold text-label">{name}</h3>
                  <div className="mt-0.5 text-xs text-label-secondary">
                    Version {version} · Status {status}
                    {isDefault ? " · Standardformular" : ""}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to={`/wartungen/kategorie-einstellungen?kategorie=${encodeURIComponent(categoryKey)}&form_id=${encodeURIComponent(formId)}`}
                    className="inline-flex h-8 items-center rounded-lg bg-card px-3 text-sm font-medium text-accent ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
                  >
                    Felder bearbeiten
                  </Link>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    loading={exportingFormId === formId}
                    onClick={() => void handleExport(formId, name)}
                  >
                    <ArrowDownTrayIcon className="size-4" aria-hidden />
                    Exportieren
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={isDefault ? "primary" : "secondary"}
                    disabled={isDefault}
                    onClick={() => void handleSetDefault(formId)}
                  >
                    {isDefault ? "Standardformular" : "Als Standard setzen"}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        dirty={newName.trim() !== ""}
        title="Neues Formular anlegen"
        size="sm"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>
              Abbrechen
            </Button>
            <Button type="button" loading={creating} onClick={() => void handleCreate()}>
              Erstellen & bearbeiten
            </Button>
          </>
        }
      >
        <TextInput
          label="Name für das neue Formular"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          error={createError ?? undefined}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleCreate();
          }}
        />
      </Dialog>
    </AppShell>
  );
}
